import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Context } from '../../../../Context';
import operationData from '../../../../../Operation/data.json';
import { fetchDiscount, saveDiscount, deleteDiscount, fetchItems } from '../../../../utils/foodDeliveryApi';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const DiscountsManagement = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || '';

  const [discounts, setDiscounts] = useState({ public: {}, private: {} });
  const [items, setItems] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [editingMode, setEditingMode] = useState(false);

  const load = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    setError('');
    try {
      const [dRes, itemsRes] = await Promise.allSettled([fetchDiscount(activeInstitution), fetchItems(activeInstitution)]);
      const dData = dRes.status === 'fulfilled' ? (dRes.value || {}) : {};
      
      console.log('[DiscountsManagement] API response:', dData);
      
      // Handle response structure: API returns { discount: { public: {...}, private: {...} } } or { discounts: {...} } or direct { public: {...}, private: {...} }
      let normalized = { public: {}, private: {} };
      
      // First check for nested 'discount' key
      if (dData?.discount && typeof dData.discount === 'object') {
        normalized.public = dData.discount.public || {};
        normalized.private = dData.discount.private || {};
      } 
      // Also check for 'discounts' key (plural)
      else if (dData?.discounts && typeof dData.discounts === 'object') {
        normalized.public = dData.discounts.public || {};
        normalized.private = dData.discounts.private || {};
      }
      // Otherwise assume direct structure
      else {
        normalized.public = dData.public || {};
        normalized.private = dData.private || {};
      }
      
      console.log('[DiscountsManagement] Normalized discounts:', normalized);
      
      setDiscounts(normalized);
      setSelectedDiscount(null);
      setEditingMode(false);
      
      const itemList = itemsRes.status === 'fulfilled' ? (Array.isArray(itemsRes.value) ? itemsRes.value : itemsRes.value?.data || []) : [];
      setItems(itemList || []);
    } catch (err) {
      setError(err?.message || 'Failed to load discounts.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeInstitution]);

  // Combine all discounts into a single list
  const combinedDiscounts = useMemo(() => {
    const entries = [];
    Object.entries(discounts.public || {}).forEach(([discountId, info]) => {
      entries.push({ discountId, discountScope: 'public', ...info });
    });
    Object.entries(discounts.private || {}).forEach(([discountId, info]) => {
      entries.push({ discountId, discountScope: 'private', ...info });
    });
    return entries;
  }, [discounts]);

  // Filter discounts by search query
  const filteredDiscounts = useMemo(() => {
    const lowerQuery = searchQuery.trim().toLowerCase();
    if (!lowerQuery) return combinedDiscounts;
    return combinedDiscounts.filter((entry) => entry.discountId.toLowerCase().includes(lowerQuery));
  }, [combinedDiscounts, searchQuery]);

  const resetForm = () => {
    setSelectedDiscount(null);
    setSelectedItemIds([]);
    setEditingMode(false);
  };

  const handleEditDiscount = (discount) => {
    setSelectedDiscount(discount);
    const ids = discount?.itemIds?.map((it) => it.itemId || it.id) || discount?.items?.map((it) => it.itemId || it.id) || [];
    setSelectedItemIds(ids);
    setEditingMode(true);
  };

  const handleSave = async () => {
    if (!selectedDiscount?.discountId?.trim()) {
      setError('Discount code is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...selectedDiscount,
        itemIds: items.filter((it) => selectedItemIds.includes(it.itemId || it.id)).map((it) => ({ itemId: it.itemId || it.id, itemName: it.itemName || it.name })),
      };
      await saveDiscount(activeInstitution, payload);
      await load();
      resetForm();
    } catch (err) {
      setError(err?.message || 'Failed to save discount.');
    } finally { setLoading(false); }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    setLoading(true);
    setError('');
    try {
      await deleteDiscount(activeInstitution);
      await load();
      resetForm();
    } catch (err) {
      setError(err?.message || 'Failed to delete discount.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Discounts & Offers</h1>
            <p className="mt-1 text-sm text-gray-500">Manage coupons and promos across the app • {activeInstitution}</p>
            {error && <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
            {loading && <div className="mt-3 text-sm text-gray-600">Loading...</div>}
          </div>

          {/* Search & Add Button */}
          <div className="bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200 flex gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
              <input
                type="text"
                placeholder="Search coupon code"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>
            <button
              onClick={() => {
                setSelectedDiscount({ discountScope: 'public', discountValue: '', discountType: 'amount', itemIds: [] });
                setSelectedItemIds([]);
                setEditingMode(true);
                setSearchQuery('');
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition"
            >
              + Add Discount
            </button>
          </div>

          {/* Discounts List */}
          {editingMode ? (
            // Edit/Create Form
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-gray-900">
                  {selectedDiscount?.discountId ? 'Edit Discount' : 'Create New Discount'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700 text-xl font-semibold"
                >
                  ✕
                </button>
              </div>

              {/* Discount Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code *</label>
                <input
                  type="text"
                  placeholder="e.g., FESTIVE20"
                  value={selectedDiscount?.discountId || ''}
                  onChange={(e) => setSelectedDiscount((d) => ({ ...(d || {}), discountId: e.target.value.toUpperCase() }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  disabled={selectedDiscount?.discountId ? true : false}
                />
              </div>

              {/* Discount Value & Type */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value *</label>
                  <input
                    type="number"
                    placeholder="Enter discount value"
                    value={selectedDiscount?.discountValue || ''}
                    onChange={(e) => setSelectedDiscount((d) => ({ ...(d || {}), discountValue: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                  <select
                    value={selectedDiscount?.discountType || 'amount'}
                    onChange={(e) => setSelectedDiscount((d) => ({ ...(d || {}), discountType: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="amount">Flat (₹)</option>
                    <option value="percent">Percentage (%)</option>
                    <option value="special">Special</option>
                  </select>
                </div>
              </div>

              {/* Min Purchase & Scope */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min. Purchase (₹)</label>
                  <input
                    type="number"
                    placeholder="e.g., 500"
                    value={selectedDiscount?.minimumPurchaseAmount || ''}
                    onChange={(e) => setSelectedDiscount((d) => ({ ...(d || {}), minimumPurchaseAmount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scope</label>
                  <select
                    value={selectedDiscount?.discountScope || 'public'}
                    onChange={(e) => setSelectedDiscount((d) => ({ ...(d || {}), discountScope: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>
              </div>

              {/* Apply to Items */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Apply to Items (Optional)</h3>
                {items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                    No items available
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.map((it) => {
                      const id = it.itemId || it.id;
                      const checked = selectedItemIds.includes(id);
                      const itemName = it.itemName || it.name || 'Unknown';
                      const itemPrice = Number(it.price || 0).toFixed(2);
                      return (
                        <label key={id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              setSelectedItemIds((prev) =>
                                e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                              )
                            }
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm font-medium text-gray-900 flex-1">{itemName}</span>
                          <span className="text-xs text-gray-500">₹{itemPrice}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                  <span className="font-semibold">{selectedItemIds.length}</span> item{selectedItemIds.length !== 1 ? 's' : ''} selected
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={loading || !selectedDiscount?.discountId || !selectedDiscount?.discountValue}
                  className="flex-1 rounded-lg bg-gray-900 text-white px-4 py-2 font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Saving...' : 'Save Discount'}
                </button>
                {selectedDiscount?.discountId && (
                  <button
                    onClick={() => handleDelete(selectedDiscount.discountId)}
                    disabled={loading}
                    className="rounded-lg bg-red-600 text-white px-4 py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={resetForm}
                  disabled={loading}
                  className="rounded-lg bg-gray-200 text-gray-900 px-4 py-2 font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // Discounts List View
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {loading ? (
                <div className="px-6 py-12 text-center text-gray-500">Loading discounts...</div>
              ) : filteredDiscounts.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  {searchQuery ? 'No discounts matching your search' : 'No discounts yet. Create one to get started.'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                  {filteredDiscounts.map((entry) => (
                    <div key={entry.discountId} className="px-6 py-4 hover:bg-gray-50 transition flex items-center justify-between group">
                      <div className="flex-1 min-w-0">
                        {/* Row 1: Code + Scope Badge */}
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{entry.discountId}</h3>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.discountScope === 'public'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {entry.discountScope}
                          </span>
                        </div>
                        
                        {/* Row 2: Discount Value + Min Purchase */}
                        <p className="text-sm text-gray-600 mb-1">
                          {entry.discountType === 'percentage' || entry.discountType === 'percent' || entry.discountType === 'special'
                            ? `${entry.discountValue}% off`
                            : `₹${entry.discountValue} off`}{' '}
                          • Min ₹{entry.minimumPurchaseAmount || 0}
                        </p>
                        
                        {/* Row 3: Additional Details */}
                        <p className="text-xs text-gray-500">
                          {entry.maximumDiscountAmount ? `Max ₹${entry.maximumDiscountAmount} • ` : ''}
                          {entry.itemIds && entry.itemIds.length > 0
                            ? `${entry.itemIds.length} item${entry.itemIds.length !== 1 ? 's' : ''}`
                            : 'All items'}
                          {entry.usageLimit ? ` • ${entry.usageLimit} /user` : ''}
                        </p>
                      </div>
                      
                      {/* Delete Button Only */}
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => handleDelete(entry.discountId)}
                          className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountsManagement;
